import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { debug } from '@/utils/debug';
import { PollingManager } from '@/components/features/GoogleAdsLinker/PollingManager';
import { TierStatusBanner } from '@/components/features/TierStatusBanner';
import { PolicyWarningBanner } from '@/components/features/PolicyWarningBanner';
import { MetaTokenExpiringBanner } from '@/components/features/MetaTokenExpiringBanner';
import { MetaIdentityChip } from '@/components/features/MetaIdentityChip';
import { GSCSettings, GSCConnectionModal } from '@/components/features/GSCIntegration';
import { GoogleConversionTrackingCard } from '@/components/features/ConversionSetup';
import { AccountExtensionsCard } from '@/components/features/AccountExtensions';
import type { GoogleAdsAccount } from '@/services/api/auth';
import { validateToken } from '@/utils/jwt';
import { formatAccountId as formatMetaAccountId } from '@/utils/metaAds';
import { SESSION_KEYS } from '@/config/constants';
import { googleAdsApi } from '@/services/api/googleAds';
import { gscApi } from '@/services/api/gsc';
import { metaAdsApi } from '@/services/api/metaAds';
import { tierApi, termsApi } from '@/services/api';
import {
  useLinkageEligibilityQuery,
  useInvalidateLinkageEligibility,
} from '@/hooks/queries/useLinkageEligibilityQuery';
import { ConfirmDialog, LoadingSpinner } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { cn } from '@/utils/cn';
import { formatAccountId, toast } from '@/utils';
import { formatSiteUrl } from '@/utils/urlUtils';
import type { TierStatus } from '@/services/api/tier';

export function Home() {
  const {
    user,
    memberUuid,
    linkedAccountsReady,
    hasLinkedGoogleAccounts: authHasLinkedAccounts,
    linkedAccountDetails: authLinkedAccountDetails,
    hasLinkedMetaAccounts,
    linkedMetaAccountDetails
  } = useAuth();
  const [showGoogleAdsLinker, setShowGoogleAdsLinker] = useState(false);
  const [showGSCConnectionModal, setShowGSCConnectionModal] = useState(false);
  const [isChangingGSCProperty, setIsChangingGSCProperty] = useState(false);
  const [devMode, setDevMode] = useState(debug.isDev());
  const [tokenValid, setTokenValid] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [unlinkingAccountId, setUnlinkingAccountId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [accountToUnlink, setAccountToUnlink] = useState<{id: string, name: string} | null>(null);
  const [unlinkingGSC, setUnlinkingGSC] = useState(false);
  const preloadLinkedAccounts = useAuthStore((state) => state.preloadLinkedAccounts);
  const queryClient = useQueryClient();

  // Use auth store values directly - they're preloaded during login
  const hasLinkedGoogleAccounts = authHasLinkedAccounts;
  const linkedAccountDetails = authLinkedAccountDetails;
  const isCheckingAccounts = !linkedAccountsReady;

  // Keep linkedAccounts for debug display only
  const linkedAccounts: GoogleAdsAccount[] = [];

  // Fetch tier status - refresh spend on initial load to get fresh data
  const { data: tierStatus, refetch: refetchTierStatus, isLoading: isTierStatusLoading } = useQuery<TierStatus>({
    queryKey: ['tier-status'],
    queryFn: async () => {
      // Refresh spend data to ensure we have the latest, then return updated status
      try {
        return await tierApi.refreshSpend();
      } catch (error) {
        debug.warn('Home: Failed to refresh spend, falling back to cached status', error);
        return await tierApi.getStatus();
      }
    },
    enabled: !!user?.memberUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch GSC connection status
  const { data: gscConnectionStatus, refetch: refetchGSCStatus, isLoading: isGSCStatusLoading } = useQuery({
    queryKey: ['gsc-connection-status'],
    queryFn: gscApi.getConnectionStatus,
    enabled: !!user?.memberUuid && hasLinkedGoogleAccounts,
  });

  // Derived state for GSC
  const isGSCLinked = !!gscConnectionStatus?.site_url;

  // Validate JWT token on mount
  useEffect(() => {
    const token = sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
    if (token) {
      const validation = validateToken(token);
      setTokenValid(validation.valid);

      if (!validation.valid) {
        debug.warn('Home: JWT token is invalid or expired', {
          expired: validation.expired,
          expiresIn: validation.expiresIn
        });
      } else {
        debug.log('Home: JWT token is valid', {
          expiresIn: validation.expiresIn,
          expiresInMinutes: Math.floor(validation.expiresIn / 60)
        });
      }
    } else {
      debug.warn('Home: No JWT token found in session');
    }
  }, []);

  // Function to refresh linked accounts (used after linking a new account)
  const refreshLinkedAccounts = React.useCallback(async () => {
    if (!user?.memberUuid) {
      return;
    }

    debug.log('Home: Refreshing linked accounts after account link');

    // Trigger a fresh preload in the auth store
    const { preloadLinkedAccounts } = useAuthStore.getState();
    await preloadLinkedAccounts(user.memberUuid);

    // Reset campaigns cache so new account's campaigns are fetched fresh
    await queryClient.resetQueries({ queryKey: ['campaigns'] });
    await queryClient.resetQueries({ queryKey: ['campaign'] });

    debug.log('Home: Linked accounts refreshed');
  }, [user?.memberUuid, queryClient]);

  // Function to show confirmation dialog
  const handleUnlinkClick = (accountId: string, accountName: string) => {
    setAccountToUnlink({ id: accountId, name: accountName });
    setConfirmDialogOpen(true);
  };

  // Function to handle the actual unlinking after confirmation
  const handleConfirmUnlink = async () => {
    if (!accountToUnlink) return;

    try {
      setUnlinkingAccountId(accountToUnlink.id);
      debug.log('Home: Unlinking account', accountToUnlink.id);

      const result = await googleAdsApi.unlinkAccount(accountToUnlink.id);

      if (result.success) {
        debug.log('Home: Account unlinked successfully', result);

        // Close the dialog
        setConfirmDialogOpen(false);
        setAccountToUnlink(null);

        // Refresh the linked accounts list
        if (user?.memberUuid) {
          await preloadLinkedAccounts(user.memberUuid);
        }

        // Reset campaigns cache so stale data isn't shown
        await queryClient.resetQueries({ queryKey: ['campaigns'] });
        await queryClient.resetQueries({ queryKey: ['campaign'] });

        // Unlinking Google potentially frees Meta linkage (free-tier
        // single-platform lock) — invalidate the eligibility query so
        // the Meta "Link" button reappears without a full page reload.
        void invalidateEligibility();

        // Exit edit mode after successful unlink
        setIsEditMode(false);

        // Show success toast
        toast.success(`Account ${accountToUnlink.name} unlinked successfully`);
      } else {
        debug.error('Home: Failed to unlink account', result);
        // Keep dialog open and show error toast
        toast.error('Failed to unlink account. Please try again.');
      }
    } catch (error) {
      debug.error('Home: Error unlinking account', error);
      toast.error('An error occurred while unlinking the account. Please try again.');
    } finally {
      setUnlinkingAccountId(null);
    }
  };

  // Function to handle dialog cancellation
  const handleCancelUnlink = () => {
    setConfirmDialogOpen(false);
    setAccountToUnlink(null);
  };

  const {
    data: eligibility,
    isLoading: isEligibilityLoading,
    isError: isEligibilityError,
    refetch: refetchEligibility,
  } = useLinkageEligibilityQuery();
  const invalidateEligibility = useInvalidateLinkageEligibility();
  // Buttons only render when BOTH (a) the platform isn't linked yet, and
  // (b) the backend allows starting a new link flow. The eligibility
  // query is the source of truth for (b) — it matches the server-side
  // gates on POST /google-ads/link-account and GET /auth/meta/authorize.
  const showGoogleButton =
    (eligibility?.can_link_google ?? false) && !hasLinkedGoogleAccounts;
  const showMetaButton =
    (eligibility?.can_link_meta ?? false) && !hasLinkedMetaAccounts;
  // Upgrade nudge: free-tier member blocked from linking at least one
  // platform. Fires whenever the backend says `upgrade_required` and any
  // platform's button is missing — including the orphan-credentials case
  // where an auto-deactivated Google/Meta credential row still trips
  // `has_linked_platform` on the backend while the frontend thinks
  // nothing's linked (see plan doc §"Design decisions" #2). Without the
  // broader condition, the user would see "Link Google Ads" but silently
  // no "Link Meta Ads" with no explanation.
  const showUpgradeNudge =
    (eligibility?.upgrade_required ?? false) &&
    (!showGoogleButton || !showMetaButton);
  // Copy sourced from the backend's `can_link_*` (not local `hasLinked*`)
  // so it stays correct in the orphan case: the platform actually blocked
  // is whichever the backend refuses, regardless of what the auth store
  // reports as "linked."
  const blockedPlatformLabel =
    eligibility?.can_link_meta === false ? 'Meta Ads' : 'Google Ads';
  const [isSubmittingMeta, setIsSubmittingMeta] = useState(false);
  const [unlinkingMetaAccountId, setUnlinkingMetaAccountId] = useState<string | null>(null);

  const handleUnlinkMetaAccount = async (metaAdAccountId: string, displayName: string) => {
    try {
      setUnlinkingMetaAccountId(metaAdAccountId);
      await metaAdsApi.disconnectAccount(metaAdAccountId);
      if (memberUuid) {
        await preloadLinkedAccounts(memberUuid);
      }
      // Disconnecting Meta may free Google linkage under the free-tier
      // single-platform lock — refresh eligibility so the UI updates.
      void invalidateEligibility();
      toast.success(`Disconnected Meta account ${displayName}`);
      setIsEditMode(false);
    } catch (error) {
      debug.error('Home: Error unlinking Meta account', error);
      toast.error('Failed to disconnect Meta account. Please try again.');
    } finally {
      setUnlinkingMetaAccountId(null);
    }
  };

  const handleConnectMeta = async () => {
    if (isSubmittingMeta) return;
    setIsSubmittingMeta(true);
    try {
      await termsApi.acceptTermsForAccountConnection();
    } catch (error) {
      debug.error('Failed to record terms acceptance for Meta connection:', error);
    }
    try {
      const { authorization_url, state } = await metaAdsApi.getAuthorizationUrl();
      window.sessionStorage.setItem(SESSION_KEYS.META_OAUTH_STATE, state);
      window.location.assign(authorization_url);
    } catch (error) {
      debug.error('Failed to start Meta OAuth flow:', error);
      toast.error("Couldn't start the Meta connection. Please try again.");
      setIsSubmittingMeta(false);
    }
  };

  // Function to handle GSC connection success
  const handleGSCConnectionSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['gsc-connection-status'] });
    queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
  };

  // Function to handle GSC unlinking
  const handleUnlinkGSC = async () => {
    try {
      setUnlinkingGSC(true);
      await gscApi.disableIntegration();
      await refetchGSCStatus();
      queryClient.invalidateQueries({ queryKey: ['gsc-settings'] });
      toast.success('Search Console unlinked successfully');
      setIsEditMode(false);
    } catch (error) {
      debug.error('Home: Error unlinking GSC', error);
      toast.error('Failed to unlink Search Console. Please try again.');
    } finally {
      setUnlinkingGSC(false);
    }
  };

  React.useEffect(() => {
    debug.log('Home: Component mounted for user', user?.memberUuid);
  }, [user]);

  // Listen for debug mode changes
  useEffect(() => {
    const updateMode = () => {
      const currentMode = debug.isDev();
      setDevMode(currentMode);
      console.log('Home: Mode updated to', currentMode ? 'DEVELOPMENT' : 'PRODUCTION');
    };
    const unsubscribe = debug.onModeChange(updateMode);
    updateMode();
    return unsubscribe;
  }, []);

  // Show loading state while tier status or GSC status is being fetched
  // Use min-h-screen and same styling as ProtectedRoute to prevent layout shift
  // Only wait for GSC if Google Ads is linked (since GSC query is only enabled then)
  const isPageLoading = isTierStatusLoading || (hasLinkedGoogleAccounts && isGSCStatusLoading);

  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" label="Checking account status" />
          <p className="text-gray-600">Checking account status...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Tier Status Banner */}
      {tierStatus && (
        <div className="mb-8">
          <TierStatusBanner
            tierStatus={tierStatus}
            onRefresh={async () => {
              try {
                // Force refresh the backend spend cache, then refetch
                await tierApi.refreshSpend();
                await refetchTierStatus();
              } catch (error) {
                debug.error('Home: Error refreshing tier status', error);
                // Still try to refetch even if refresh fails
                await refetchTierStatus();
              }
            }}
          />
        </div>
      )}

      {/* Policy Warning Banner */}
      <PolicyWarningBanner className="mb-8" />

      {/* Meta Token Expiring Banner — surfaces 7 days before token expiry
          per META_INTEGRATION_PLAN.md §4.2. Reads from
          authStore.linkedMetaAccountDetails populated by preloadLinkedAccounts. */}
      <MetaTokenExpiringBanner className="mb-8" />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-gray-50 to-amber-50/50 border border-gray-200 py-10 px-6 rounded-lg mb-12 text-center">
        <div className="flex justify-center mb-6">
          <Logo variant="color" size="xl" className="!h-[60px] translate-x-[10px]" />
        </div>
        <p className="text-2xl md:text-3xl mb-6 text-flouri-darkGray">
          Let's get to{' '}
          <span
            className="font-bold animate-gradient-shift"
            style={{
              background: 'linear-gradient(90deg, #ff4770 0%, #cc1a40 50%, #ff4770 100%)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            flourishing
          </span>
          .
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center sm:items-stretch">
          {/* Sequential Linking CTAs - stacked, with Meta below Google/GSC.
              Also renders the eligibility loading + error affordances so
              an unlinked user isn't stranded on a blank Home while the
              backend responds (or hasn't). */}
          {(showGoogleButton ||
            (hasLinkedGoogleAccounts && !isGSCLinked) ||
            showMetaButton ||
            showUpgradeNudge ||
            ((isEligibilityLoading || isEligibilityError) &&
              !hasLinkedGoogleAccounts &&
              !hasLinkedMetaAccounts)) && (
            <div className="flex flex-col gap-4">
              {/* Eligibility loading — show a placeholder in the CTA slot
                  for users who don't yet have any linkage, so they don't
                  see an empty column while the query resolves. Users
                  already linked to at least one platform skip this (they
                  have the "View Your Campaigns" button on the right and
                  no pending CTAs). */}
              {isEligibilityLoading &&
                !eligibility &&
                !hasLinkedGoogleAccounts &&
                !hasLinkedMetaAccounts && (
                  <div
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600 gap-2"
                    role="status"
                    aria-live="polite"
                  >
                    <svg
                      className="w-4 h-4 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Checking plan eligibility…
                  </div>
                )}
              {/* Eligibility fetch failed — actionable retry state so the
                  unlinked user isn't silently left with no CTAs. */}
              {isEligibilityError &&
                !eligibility &&
                !hasLinkedGoogleAccounts &&
                !hasLinkedMetaAccounts && (
                  <div
                    className="w-full px-6 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800"
                    role="alert"
                  >
                    <p className="font-medium">Couldn't check plan eligibility.</p>
                    <p className="mt-1 text-red-700">
                      Please try again in a moment.
                    </p>
                    <button
                      type="button"
                      onClick={() => void refetchEligibility()}
                      className="mt-2 inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
                    >
                      Retry
                    </button>
                  </div>
                )}
              {showGoogleButton ? (
                // State 1: No Google Ads account - show Ads link button
                <button
                  onClick={() => setShowGoogleAdsLinker(true)}
                  className={cn(
                    'group inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg shadow-lg',
                    'bg-blue-700 text-white',
                    'transition-all duration-200 ease-out',
                    'hover:bg-blue-800 hover:shadow-xl hover:-translate-y-0.5',
                    'active:translate-y-0 active:shadow-lg active:bg-blue-900'
                  )}
                >
                  Link Google Ads
                  <svg
                    className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:rotate-12"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </button>
              ) : hasLinkedGoogleAccounts && !isGSCLinked ? (
                // State 2: Google Ads linked, no GSC - show GSC link button
                <button
                  onClick={() => {
                    setIsChangingGSCProperty(false);
                    setShowGSCConnectionModal(true);
                  }}
                  className={cn(
                    'group inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg shadow-lg',
                    'bg-blue-700 text-white',
                    'transition-all duration-200 ease-out',
                    'hover:bg-blue-800 hover:shadow-xl hover:-translate-y-0.5',
                    'active:translate-y-0 active:shadow-lg active:bg-blue-900'
                  )}
                >
                  Link Google Search Console
                  <svg
                    className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:rotate-12"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              ) : null}

              {showMetaButton && (
                <button
                  onClick={handleConnectMeta}
                  disabled={isSubmittingMeta}
                  className={cn(
                    'group inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg shadow-lg',
                    'bg-blue-700 text-white',
                    'transition-all duration-200 ease-out',
                    'hover:bg-blue-800 hover:shadow-xl hover:-translate-y-0.5',
                    'active:translate-y-0 active:shadow-lg active:bg-blue-900',
                    'disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg'
                  )}
                >
                  {isSubmittingMeta ? 'Processing...' : 'Link Meta Ads'}
                  {!isSubmittingMeta && (
                    <svg
                      className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:rotate-12"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  )}
                </button>
              )}
              {showUpgradeNudge && (
                // Light-grey pill with a border so it reads as a distinct
                // secondary CTA next to the primary blue Link buttons —
                // the "Pro" word carries the brand accent (flouri red,
                // bold) to signal what's being upsold. Copy names the
                // blocked platform (more actionable than the generic
                // "link both"). Placeholder link target
                // (/settings?tab=membership); the Pro branch swaps this
                // for the checkout entry point.
                <Link
                  to="/settings?tab=membership"
                  className={cn(
                    'group inline-flex items-center justify-center px-6 py-3 font-medium rounded-lg text-black shadow-lg',
                    'bg-white border border-gray-300',
                    'transition-all duration-200 ease-out',
                    'hover:bg-gray-50 hover:shadow-xl hover:-translate-y-0.5',
                    'active:translate-y-0 active:shadow-lg active:bg-gray-100'
                  )}
                >
                  Upgrade to{' '}
                  <span
                    className="font-bold mx-1"
                    style={{ color: '#cc1a40' }}
                  >
                    Pro
                  </span>{' '}
                  to link {blockedPlatformLabel}
                </Link>
              )}
            </div>
          )}

          {/* View Campaigns Button - shown only when there's somewhere to go. Stretches to match the linking column height so it visually "spans" both Google + Meta. */}
          {!isCheckingAccounts && (hasLinkedGoogleAccounts || hasLinkedMetaAccounts) && (
            <Link
              to="/account"
              className={cn(
                'group inline-flex items-center justify-center px-6 py-3 font-semibold rounded-lg shadow-lg',
                'bg-white text-blue-700 border border-gray-300',
                'transition-all duration-200 ease-out',
                'hover:bg-blue-50 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-lg'
              )}
            >
              View Your Campaigns
              <svg
                className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          )}
        </div>
      </div>

      {/* Linked Accounts Section. Shown when *any* provider is linked —
          Google Ads, Meta, or GSC. A beta user who links Meta before
          Google Ads still needs the card to render so they can see and
          unlink their Meta account from the dashboard. */}
      {((hasLinkedGoogleAccounts && linkedAccountDetails.length > 0) ||
        hasLinkedMetaAccounts ||
        isGSCLinked) && (
        <div className={cn(
          'mb-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6',
          'transition-shadow duration-300 hover:shadow-md'
        )}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Linked Accounts</h3>
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className={cn(
                'p-2 text-gray-600 rounded-lg',
                'transition-all duration-200',
                'hover:text-gray-900 hover:bg-gray-100 hover:scale-110',
                'active:scale-95',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              )}
              aria-label={isEditMode ? "Done editing accounts" : "Edit linked accounts"}
              title={isEditMode ? "Done editing" : "Edit accounts"}
            >
              {isEditMode ? (
                <svg className="w-5 h-5 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-5 h-5 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              )}
            </button>
          </div>

          <div className="flex flex-col space-y-1">
            {/* Google Ads Accounts */}
            {linkedAccountDetails.map((account) => (
              <div
                key={account.id}
                className={cn(
                  'text-gray-700 min-h-[40px] px-3 py-2 -mx-3 rounded-lg',
                  'transition-colors duration-150',
                  'hover:bg-gray-50'
                )}
              >
                {/* Desktop/Tablet: single line */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500 mr-2">Google Ads</span>
                    <span className="text-gray-900">{account.name}</span>
                    <span className="ml-3 font-mono text-gray-500">{formatAccountId(account.id)}</span>
                  </div>
                  <button
                    onClick={() => handleUnlinkClick(account.id, account.name)}
                    disabled={unlinkingAccountId === account.id || !isEditMode}
                    className={cn(
                      'px-3 py-1 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded',
                      'transition-all duration-200',
                      'hover:bg-red-100 hover:border-red-300 hover:scale-105',
                      'active:scale-95',
                      'disabled:cursor-not-allowed',
                      'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                      isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none',
                      unlinkingAccountId === account.id && 'opacity-50'
                    )}
                    aria-label={`Unlink account ${formatAccountId(account.id)} - ${account.name}`}
                    aria-busy={unlinkingAccountId === account.id}
                    aria-hidden={!isEditMode}
                    tabIndex={isEditMode ? 0 : -1}
                  >
                    {unlinkingAccountId === account.id ? 'Unlinking...' : 'Unlink'}
                  </button>
                </div>
                {/* Mobile: two lines - label on first, ID + name + button on second */}
                <div className="sm:hidden">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500">Google Ads</span>
                  </div>
                  <div className="flex items-center justify-between pl-7 mt-1 gap-2">
                    <span className="text-sm min-w-0 flex-1">
                      <span className="text-gray-900 truncate inline-block max-w-[140px] align-bottom">{account.name}</span>
                      <span className="ml-2 font-mono text-gray-500">{formatAccountId(account.id)}</span>
                    </span>
                    <button
                      onClick={() => handleUnlinkClick(account.id, account.name)}
                      disabled={unlinkingAccountId === account.id || !isEditMode}
                      className={cn(
                        'px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded',
                        'transition-all duration-200',
                        'hover:bg-red-100 hover:border-red-300 hover:scale-105',
                        'active:scale-95',
                        'disabled:cursor-not-allowed',
                        'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                        isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none',
                        unlinkingAccountId === account.id && 'opacity-50'
                      )}
                      aria-label={`Unlink account ${formatAccountId(account.id)} - ${account.name}`}
                      aria-busy={unlinkingAccountId === account.id}
                      aria-hidden={!isEditMode}
                      tabIndex={isEditMode ? 0 : -1}
                    >
                      {unlinkingAccountId === account.id ? 'Unlinking...' : 'Unlink'}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Meta Ads Accounts */}
            {linkedMetaAccountDetails.map((account) => {
              // Display label: use the Business Portfolio name only. Meta's
              // `account.name` for personal ad accounts defaults to the
              // owner's FB profile name (e.g. "Michelle Leister"), which we
              // never want surfaced on the Dashboard. If business_name is
              // unset, render just the ad account ID.
              const displayName = account.businessName;
              // `act_` is a Meta API namespace prefix — Ads Manager URLs
              // and UI both elide it, so we do too. account.id keeps the
              // prefix for backend calls (the API still expects act_<digits>).
              // Centralized in `utils/metaAds.ts:formatAccountId` so MetaCampaignCard
              // and the Meta detail page render the same display form.
              const displayId = formatMetaAccountId(account.id);
              return (
              <div
                key={`meta-${account.id}`}
                className={cn(
                  'text-gray-700 min-h-[40px] px-3 py-2 -mx-3 rounded-lg',
                  'transition-colors duration-150',
                  'hover:bg-gray-50'
                )}
              >
                {/* Desktop/Tablet: single line */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500 mr-2">Meta Ads</span>
                    {displayName && <span className="text-gray-900">{displayName}</span>}
                    <span className={cn('font-mono text-gray-500', displayName && 'ml-3')}>{displayId}</span>
                  </div>
                  <button
                    onClick={() => handleUnlinkMetaAccount(account.id, displayName || displayId)}
                    disabled={unlinkingMetaAccountId === account.id || !isEditMode}
                    className={cn(
                      'px-3 py-1 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded',
                      'transition-all duration-200',
                      'hover:bg-red-100 hover:border-red-300 hover:scale-105',
                      'active:scale-95',
                      'disabled:cursor-not-allowed',
                      'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                      isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none',
                      unlinkingMetaAccountId === account.id && 'opacity-50'
                    )}
                    aria-label={`Unlink Meta account ${displayId}${displayName ? ` - ${displayName}` : ''}`}
                    aria-busy={unlinkingMetaAccountId === account.id}
                    aria-hidden={!isEditMode}
                    tabIndex={isEditMode ? 0 : -1}
                  >
                    {unlinkingMetaAccountId === account.id ? 'Unlinking...' : 'Unlink'}
                  </button>
                </div>
                {/* Mobile: two lines - label on first, ID + name + button on second */}
                <div className="sm:hidden">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500">Meta Ads</span>
                  </div>
                  <div className="flex items-center justify-between pl-7 mt-1 gap-2">
                    <span className="text-sm min-w-0 flex-1">
                      {displayName && (
                        <span className="text-gray-900 truncate inline-block max-w-[140px] align-bottom">{displayName}</span>
                      )}
                      <span className={cn('font-mono text-gray-500', displayName && 'ml-2')}>{displayId}</span>
                    </span>
                    <button
                      onClick={() => handleUnlinkMetaAccount(account.id, displayName || displayId)}
                      disabled={unlinkingMetaAccountId === account.id || !isEditMode}
                      className={cn(
                        'px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded',
                        'transition-all duration-200',
                        'hover:bg-red-100 hover:border-red-300 hover:scale-105',
                        'active:scale-95',
                        'disabled:cursor-not-allowed',
                        'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                        isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none',
                        unlinkingMetaAccountId === account.id && 'opacity-50'
                      )}
                      aria-label={`Unlink Meta account ${displayId}${displayName ? ` - ${displayName}` : ''}`}
                      aria-busy={unlinkingMetaAccountId === account.id}
                      aria-hidden={!isEditMode}
                      tabIndex={isEditMode ? 0 : -1}
                    >
                      {unlinkingMetaAccountId === account.id ? 'Unlinking...' : 'Unlink'}
                    </button>
                  </div>
                </div>
              </div>
              );
            })}

            {/* Meta identity chip — rendered once BELOW the Meta
                accounts list (all rows share the same FB identity).
                Sourced from `meta_user_name` + a Graph picture URL
                derived server-side from `meta_user_id`. Surfaces
                `public_profile` so the user can verify which Meta
                identity they linked as — also the on-screen evidence
                Meta App Review expects for `public_profile`. The
                chip itself picks the first entry with a non-null
                `metaUserName` from the list; nothing renders when
                every entry's name is null (legacy pre-013 rows
                pending backfill). Placed AFTER the Meta rows (not
                between Google and Meta) so it visually reads as part
                of the Meta block — a chip sandwiched between
                platforms reads ambiguously as belonging to either. */}
            <MetaIdentityChip details={linkedMetaAccountDetails} />

            {/* GSC Account — show ONLY when linked. The "Add Google
                Search Console" affordance previously rendered as the
                else-branch here is intentionally removed: the same
                discovery surface exists in the action-button group
                above (the "Link Google Search Console" button at the
                hasLinkedGoogleAccounts conditional), so this slot
                only needs to render the connected state. Dropping the
                else-branch also removes a chip that visually sat just
                below the Meta block and read ambiguously as
                Meta-associated. */}
            {isGSCLinked && (
              <div
                className={cn(
                  'text-gray-700 min-h-[40px] px-3 py-2 -mx-3 rounded-lg',
                  'transition-colors duration-150',
                  'hover:bg-gray-50'
                )}
              >
                {/* Desktop/Tablet: single line */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500 mr-2">Google Search Console</span>
                    <span className="text-gray-900">{formatSiteUrl(gscConnectionStatus?.site_url || '')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setIsChangingGSCProperty(true);
                        setShowGSCConnectionModal(true);
                      }}
                      className={cn(
                        'px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded',
                        'transition-all duration-200',
                        'hover:bg-gray-100 hover:border-gray-300',
                        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                        isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none'
                      )}
                      aria-hidden={!isEditMode}
                      tabIndex={isEditMode ? 0 : -1}
                    >
                      Change
                    </button>
                    <button
                      onClick={handleUnlinkGSC}
                      disabled={unlinkingGSC || !isEditMode}
                      className={cn(
                        'px-3 py-1 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded',
                        'transition-all duration-200',
                        'hover:bg-red-100 hover:border-red-300 hover:scale-105',
                        'active:scale-95',
                        'disabled:cursor-not-allowed',
                        'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                        isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none',
                        unlinkingGSC && 'opacity-50'
                      )}
                      aria-hidden={!isEditMode}
                      tabIndex={isEditMode ? 0 : -1}
                    >
                      {unlinkingGSC ? 'Unlinking...' : 'Unlink'}
                    </button>
                  </div>
                </div>
                {/* Mobile: two lines - label on first, URL + buttons on second */}
                <div className="sm:hidden">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 mr-2 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-gray-500">Google Search Console</span>
                  </div>
                  <div className="flex items-center justify-between pl-7 mt-1 gap-2">
                    <span className="text-gray-900 text-sm truncate min-w-0 flex-1">{formatSiteUrl(gscConnectionStatus?.site_url || '')}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => {
                          setIsChangingGSCProperty(true);
                          setShowGSCConnectionModal(true);
                        }}
                        className={cn(
                          'px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded',
                          'transition-all duration-200',
                          'hover:bg-gray-100 hover:border-gray-300',
                          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                          isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none'
                        )}
                        aria-hidden={!isEditMode}
                        tabIndex={isEditMode ? 0 : -1}
                      >
                        Change
                      </button>
                      <button
                        onClick={handleUnlinkGSC}
                        disabled={unlinkingGSC || !isEditMode}
                        className={cn(
                          'px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded',
                          'transition-all duration-200',
                          'hover:bg-red-100 hover:border-red-300 hover:scale-105',
                          'active:scale-95',
                          'disabled:cursor-not-allowed',
                          'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                          isEditMode ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-2 pointer-events-none',
                          unlinkingGSC && 'opacity-50'
                        )}
                        aria-hidden={!isEditMode}
                        tabIndex={isEditMode ? 0 : -1}
                      >
                        {unlinkingGSC ? 'Unlinking...' : 'Unlink'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Google Ads Conversion Tracking — unified card with online + offline.
          The offline section appears only for members in
          OFFLINE_CONVERSIONS_BETA_MEMBER_IDS; that gating happens inside the
          card via useOfflineConversionsAccess, so no prop wiring is needed. */}
      {hasLinkedGoogleAccounts && linkedAccountDetails.length > 0 && (
        <div className="mb-12">
          <GoogleConversionTrackingCard
            googleAdsAccountId={linkedAccountDetails[0].id}
          />
        </div>
      )}

      {/* Account Extensions Card - show after accounts are linked */}
      {hasLinkedGoogleAccounts && linkedAccountDetails.length > 0 && (
        <div className="mb-12">
          <AccountExtensionsCard
            googleAdsAccountId={linkedAccountDetails[0].id}
            linkedAccounts={linkedAccountDetails.map(acc => ({ id: acc.id, name: acc.name }))}
          />
        </div>
      )}

      {/* GSC Settings Section - only show when GSC is linked (settings controls only) */}
      {isGSCLinked && (
        <div className="mb-12">
          <GSCSettings showLinkStatus={false} />
        </div>
      )}

      {devMode && (
        <div className="mt-12 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">🔧 Debug Information</h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold mb-1">User Data:</h4>
              <pre className="text-xs bg-gray-800 text-white p-2 rounded overflow-auto">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">JWT Token Status:</h4>
              <div className="text-xs bg-gray-800 text-white p-2 rounded">
                <div className={`${tokenValid ? 'text-green-400' : 'text-red-400'}`}>
                  Valid: {tokenValid ? '✓ Yes' : '✗ No'}
                </div>
                {sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN) && (() => {
                  const token = sessionStorage.getItem(SESSION_KEYS.AUTH_TOKEN)!;
                  const validation = validateToken(token);
                  return (
                    <>
                      <div>Expires in: {Math.floor(validation.expiresIn / 60)} minutes</div>
                      <div>Subject: {validation.payload?.sub || 'N/A'}</div>
                    </>
                  );
                })()}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-1">Google Ads Accounts ({linkedAccounts.length}):</h4>
              <pre className="text-xs bg-gray-800 text-white p-2 rounded overflow-auto">
                {JSON.stringify(linkedAccounts, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Google Ads Linking Flow */}
      <PollingManager
        isOpen={showGoogleAdsLinker}
        onClose={() => {
          setShowGoogleAdsLinker(false);
        }}
        onAccountLinked={async () => {
          // Refresh account data immediately when account is linked
          await refreshLinkedAccounts();
        }}
      />

      {/* Unlink Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={handleCancelUnlink}
        onConfirm={handleConfirmUnlink}
        title="Unlink Google Ads Account"
        message={accountToUnlink ? `Are you sure you want to unlink account ${formatAccountId(accountToUnlink.id)} (${accountToUnlink.name})? This will remove access to campaigns and data from this account.` : ''}
        confirmText="Unlink Account"
        cancelText="Cancel"
        variant="danger"
        isLoading={unlinkingAccountId !== null}
      />

      {/* GSC Connection Modal */}
      <GSCConnectionModal
        isOpen={showGSCConnectionModal}
        onClose={() => {
          setShowGSCConnectionModal(false);
          setIsChangingGSCProperty(false);
        }}
        onConnected={handleGSCConnectionSuccess}
        isChangingProperty={isChangingGSCProperty}
      />
    </div>
  );
}